'use client';

import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import { Bell, MessageSquare, Check, ExternalLink, X, Sparkles } from 'lucide-react';
import api, { notificationsApi } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { isPublicAuthRoute } from '@/lib/public-routes';

type UnreadMessageSummary = {
  id: string;
  name: string;
  unread_count: number;
  latest_message?: {
    message: string;
    created_at: string;
    sender?: { name: string; avatar?: string | null };
  } | null;
  event?: { id: string; title: string; slug: string } | null;
};

type NotificationItem = {
  id: string;
  type?: string;
  title?: string;
  body?: string;
  read_at?: string | null;
  created_at?: string;
};

async function fetchWithRetryOnce<T>(fn: () => Promise<T>, backoffMs = 1200): Promise<T | null> {
  try {
    return await fn();
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 401) {
      throw err;
    }
    // Do not retry client errors or rate limits (403, 404, 422, 429, 500+)
    if (status === 403 || status === 404 || status === 422 || status === 429 || status >= 500) {
      return null;
    }
    await new Promise((resolve) => setTimeout(resolve, backoffMs));
    try {
      return await fn();
    } catch (retryErr: any) {
      if (retryErr?.response?.status === 401) {
        throw retryErr;
      }
      return null;
    }
  }
}

const DISMISSED_CHAT_POPUPS_KEY = 'enthioshub_dismissed_chat_popups';
const SHOWN_CHAT_POPUPS_KEY = 'enthioshub_shown_chat_popups';

function getPopupKey(conv: UnreadMessageSummary): string {
  const latestPart = conv.latest_message?.created_at || conv.latest_message?.message || 'seen';
  return `${conv.id}_${latestPart}`;
}

function isMessageOld(conv: UnreadMessageSummary): boolean {
  const createdAt = conv.latest_message?.created_at;
  if (!createdAt) return true;
  const time = new Date(createdAt).getTime();
  if (isNaN(time)) return false;
  // If message was created more than 1 hour ago, it is old and shouldn't pop up as a fresh alert
  return Date.now() - time > 60 * 60 * 1000;
}

function isPopupDismissed(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(DISMISSED_CHAT_POPUPS_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    return list.includes(key);
  } catch {
    return false;
  }
}

function markPopupDismissed(key: string): void {
  if (typeof window === 'undefined' || !key) return;
  try {
    const raw = localStorage.getItem(DISMISSED_CHAT_POPUPS_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    if (!list.includes(key)) {
      list.push(key);
      localStorage.setItem(DISMISSED_CHAT_POPUPS_KEY, JSON.stringify(list.slice(-100)));
    }
  } catch {}
}

function isPopupShown(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(SHOWN_CHAT_POPUPS_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    return list.includes(key);
  } catch {
    return false;
  }
}

function markPopupShown(key: string): void {
  if (typeof window === 'undefined' || !key) return;
  try {
    const raw = localStorage.getItem(SHOWN_CHAT_POPUPS_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    if (!list.includes(key)) {
      list.push(key);
      localStorage.setItem(SHOWN_CHAT_POPUPS_KEY, JSON.stringify(list.slice(-100)));
    }
  } catch {}
}

export function NotificationBellCenter() {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState<UnreadMessageSummary[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadMessagesTotal, setUnreadMessagesTotal] = useState(0);
  const [unreadNotifsTotal, setUnreadNotifsTotal] = useState(0);
  const [toastMessage, setToastMessage] = useState<{
    id: string;
    key: string;
    title: string;
    body: string;
    href?: string;
  } | null>(null);

  const prevUnreadRef = useRef<number>(0);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);
  const backoffDelayRef = useRef<number>(30000);
  const isPollingStoppedRef = useRef<boolean>(false);

  const handleDismissToast = (key?: string) => {
    if (key) {
      markPopupDismissed(key);
    }
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(null);
  };

  const authChecking = useAuthStore((state) => state.authChecking);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const isAuthed = !authChecking && isAuthenticated && Boolean(user?.id || user?.email);

  const pollNotifications = async () => {
    if (isPollingStoppedRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (
        isPublicAuthRoute(path) ||
        path.startsWith('/participate') ||
        path.startsWith('/quiz') ||
        path.startsWith('/register') ||
        path.startsWith('/signup') ||
        path.startsWith('/login') ||
        path.startsWith('/forgot-password') ||
        path.startsWith('/verify-email')
      ) {
        return;
      }
    }

    const { authChecking: checking, isAuthenticated: authed, user: currentUser } = useAuthStore.getState();
    if (checking || !authed || !currentUser) return;

    let hasError = false;
    let is401 = false;

    try {
      // 1. Fetch Member Messages Inbox with in-flight deduplication and short cache
      const msgRes = await fetchWithRetryOnce(() =>
        api.get<{
          success: boolean;
          data: UnreadMessageSummary[];
          meta?: { unread_total?: number };
        }>('/my/messages', { cacheTtlMs: 15000, suppressServerErrorToast: true, silent: true } as any)
      );

      if (msgRes?.data?.success) {
        const convList = msgRes.data.data || [];
        const unreadConvs = convList.filter((c) => (c.unread_count ?? 0) > 0);
        setConversations(unreadConvs);

        const msgTotal = msgRes.data.meta?.unread_total ?? unreadConvs.reduce((acc, c) => acc + (c.unread_count ?? 0), 0);
        setUnreadMessagesTotal(msgTotal);

        // Check for new incoming message toast trigger
        const newest = unreadConvs[0];
        if (newest && msgTotal > 0) {
          const popupKey = getPopupKey(newest);
          const alreadyDismissed = isPopupDismissed(popupKey);
          const alreadyShown = isPopupShown(popupKey);
          const oldMessage = isMessageOld(newest);

          // If message is old, silently record so it never pops
          if (oldMessage && (!alreadyDismissed || !alreadyShown)) {
            markPopupDismissed(popupKey);
            markPopupShown(popupKey);
          }

          // Only pop once: if cancelled or opened or already shown, never pop again; and never pop old messages
          if (!oldMessage && !alreadyDismissed && !alreadyShown) {
            markPopupShown(popupKey);
            markPopupDismissed(popupKey);

            const currentUser = useAuthStore.getState().user;
            const role = String(currentUser?.role || currentUser?.user_type || '').toLowerCase();
            let targetHref = '/dashboard/messages';
            const n = newest as any;
            const isSupportOrAdmin =
              (n.type && String(n.type).includes('admin')) ||
              (n.name && String(n.name).toLowerCase().includes('admin')) ||
              (n.category && String(n.category).toLowerCase().includes('admin')) ||
              (n.category && String(n.category).toLowerCase().includes('support'));

            if (isSupportOrAdmin) {
              targetHref = '/dashboard/support';
            } else if (role === 'jury' || role === 'judge') {
              targetHref = '/jury/messages';
            } else if (role === 'organization_admin' || role === 'organization_manager') {
              targetHref = '/dashboard/organization/messages';
            } else if (role === 'organizer' || role === 'individual_organizer') {
              targetHref = '/dashboard/organizer/org-chat';
            } else if (role === 'admin' || role === 'super_admin' || role === 'system_admin') {
              targetHref = '/dashboard/admin/messages';
            } else {
              targetHref = '/dashboard/messages';
            }

            setToastMessage({
              id: newest.id,
              key: popupKey,
              title: newest.name || newest.latest_message?.sender?.name || 'New Message',
              body: newest.latest_message?.message || 'You received a new message.',
              href: targetHref,
            });

            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            toastTimerRef.current = setTimeout(() => {
              markPopupDismissed(popupKey);
              setToastMessage(null);
            }, 8000);
          }
        }
        prevUnreadRef.current = msgTotal;
      } else if (!msgRes) {
        hasError = true;
        backoffDelayRef.current = Math.max(backoffDelayRef.current, 60000);
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        is401 = true;
      }
      if (err?.response?.status === 429) {
        backoffDelayRef.current = Math.max(backoffDelayRef.current, 60000);
      }
      hasError = true;
    }

    if (!is401) {
      try {
        // 2. Fetch System Notifications with in-flight deduplication and short cache
        const notifRes = await fetchWithRetryOnce(() =>
          notificationsApi.list({ page: 1 })
        );
        if (notifRes?.data?.success) {
          const raw = notifRes.data.data ?? notifRes.data;
          const list: NotificationItem[] = Array.isArray(raw) ? raw : raw?.data || [];
          setNotifications(list);
          const unreadCount = list.filter((n: NotificationItem) => !n.read_at).length;
          setUnreadNotifsTotal(unreadCount);
        } else if (!notifRes) {
          hasError = true;
          backoffDelayRef.current = Math.max(backoffDelayRef.current, 60000);
        }
      } catch (err: any) {
        if (err?.response?.status === 401) {
          is401 = true;
        }
        if (err?.response?.status === 429) {
          backoffDelayRef.current = Math.max(backoffDelayRef.current, 60000);
        }
        hasError = true;
      }
    }

    if (is401) {
      // Permanently cease polling on 401 until re-authenticated
      isPollingStoppedRef.current = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    if (hasError) {
      // Exponential backoff up to 5 minutes (300,000 ms), starting from at least 30,000 ms
      backoffDelayRef.current = Math.min(Math.max(backoffDelayRef.current * 2, 30000), 300000);
    } else {
      backoffDelayRef.current = 30000;
    }

    // Schedule next polling cycle if tab is still visible and not stopped
    scheduleNextPoll(backoffDelayRef.current);
  };

  const scheduleNextPoll = (delay: number) => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (isPollingStoppedRef.current) return;
    pollTimerRef.current = setTimeout(() => {
      void pollNotifications();
    }, delay);
  };

  useEffect(() => {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (!isAuthed || isPublicAuthRoute(currentPath)) {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    isPollingStoppedRef.current = false;
    backoffDelayRef.current = 30000;

    // Immediately poll if tab is visible
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      void pollNotifications();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const { authChecking: checking, isAuthenticated: authed, user: currentUser } = useAuthStore.getState();
        const activePath = typeof window !== 'undefined' ? window.location.pathname : '';
        if (!checking && authed && currentUser && !isPollingStoppedRef.current && !isPublicAuthRoute(activePath)) {
          void pollNotifications();
        }
      } else {
        if (pollTimerRef.current) {
          clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      }
    };

    const handleSessionExpired = () => {
      isPollingStoppedRef.current = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('auth:session-expired', handleSessionExpired);
    window.addEventListener('auth:unauthenticated', handleSessionExpired);

    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      window.removeEventListener('auth:unauthenticated', handleSessionExpired);
    };
  }, [authChecking, isAuthenticated, isAuthed]);

  const totalUnread = unreadMessagesTotal + unreadNotifsTotal;

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead().catch(() => null);
      setUnreadNotifsTotal(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
    } catch {
      // ignore
    }
  };

  const getRoleMessageHref = (conv?: UnreadMessageSummary | null) => {
    const currentUser = useAuthStore.getState().user;
    const role = String(currentUser?.role || currentUser?.user_type || '').toLowerCase();
    if (conv) {
      const n = conv as any;
      const isSupportOrAdmin =
        (n.type && String(n.type).includes('admin')) ||
        (n.name && String(n.name).toLowerCase().includes('admin')) ||
        (n.category && String(n.category).toLowerCase().includes('admin')) ||
        (n.category && String(n.category).toLowerCase().includes('support'));
      if (isSupportOrAdmin) return '/dashboard/support';
    }
    if (role === 'jury' || role === 'judge') return '/jury/messages';
    if (role === 'organization_admin' || role === 'organization_manager') return '/dashboard/organization/messages';
    if (role === 'organizer' || role === 'individual_organizer') return '/dashboard/organizer/org-chat';
    if (role === 'admin' || role === 'super_admin' || role === 'system_admin') return '/dashboard/admin/messages';
    return '/dashboard/messages';
  };

  if (!isAuthed) {
    return null;
  }

  return (
    <div className="relative">
      {/* Header Bell Button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Notifications & Messages"
        aria-label="Notifications & Messages"
        className="relative flex h-10 w-10 items-center justify-center rounded-xl text-[#2D3536] hover:bg-[#FAF8F2] transition active:scale-95 cursor-pointer border border-transparent hover:border-[#E2DFD5]"
      >
        <Bell className="h-5 w-5 text-[#2D3536]" />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-extrabold text-white shadow-md animate-pulse">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {/* Floating Popup Toast for New Messages */}
      {toastMessage && (
        <div className="fixed top-16 right-5 z-50 flex max-w-sm items-start gap-3 rounded-2xl border border-emerald-200 bg-white/95 p-4 shadow-2xl backdrop-blur-md transition-all animate-in slide-in-from-top-4 duration-300">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-800 font-bold text-sm shadow-2xs">
            <MessageSquare className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold text-gray-900 truncate">{toastMessage.title}</p>
              <button
                type="button"
                onClick={() => handleDismissToast(toastMessage.key)}
                className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                title="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="mt-0.5 text-xs text-gray-600 line-clamp-2">{toastMessage.body}</p>
            <Link
              href={toastMessage.href || '/dashboard/notifications'}
              onClick={() => handleDismissToast(toastMessage.key)}
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 hover:text-emerald-800 hover:underline"
            >
              Open Messages <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      {/* Dropdown Notification Center Panel */}
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-transparent" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 z-50 w-80 sm:w-96 overflow-hidden rounded-2xl border border-[#E2DFD5] bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-150">
            {/* Dropdown Header */}
            <div className="flex items-center justify-between border-b border-[#E2DFD5] bg-[#FDFBF7] px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-[#1B2A24]">Notifications</span>
                {totalUnread > 0 && (
                  <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                    {totalUnread} new
                  </span>
                )}
              </div>
              {unreadNotifsTotal > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="text-xs font-semibold text-[#5F6F52] hover:underline cursor-pointer"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Notification Content List */}
            <div className="max-h-80 overflow-y-auto divide-y divide-[#E2DFD5]/50 custom-scrollbar">
              {/* Unread Chat Messages Section */}
              {conversations.length > 0 && (
                <div className="bg-[#FAF8F2]/50 p-2">
                  <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#5F6F52]">
                    Unread Messages ({unreadMessagesTotal})
                  </p>
                  {conversations.map((conv) => (
                    <Link
                      key={conv.id}
                      href={getRoleMessageHref(conv)}
                      onClick={() => setOpen(false)}
                      className="flex items-start gap-3 rounded-xl p-2.5 transition hover:bg-white"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#5F6F52] text-white">
                        <MessageSquare className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1">
                          <p className="truncate text-xs font-bold text-[#1B2A24]">{conv.name}</p>
                          <span className="shrink-0 rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-extrabold text-white">
                            {conv.unread_count}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-[#5F6E66] truncate">
                          {conv.latest_message?.message || 'New message in group'}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* System Notifications List */}
              {notifications.length > 0 ? (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-3 transition ${!notif.read_at ? 'bg-[#FAF8F2]' : 'bg-white'}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
                        <Sparkles className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-[#1B2A24]">{notif.title || 'Notification'}</p>
                        {notif.body && <p className="mt-0.5 text-xs text-[#5F6E66] line-clamp-2">{notif.body}</p>}
                      </div>
                    </div>
                  </div>
                ))
              ) : conversations.length === 0 ? (
                <div className="p-6 text-center text-xs text-[#5F6E66]">
                  <Check className="mx-auto mb-2 h-6 w-6 text-emerald-600 opacity-60" />
                  <p className="font-semibold text-[#1B2A24]">All caught up!</p>
                  <p className="mt-0.5">No unread notifications or messages.</p>
                </div>
              ) : null}
            </div>

            {/* Dropdown Footer Links */}
            <div className="flex items-center justify-between border-t border-[#E2DFD5] bg-[#FDFBF7] px-4 py-2.5 text-xs font-bold">
              <Link
                href={getRoleMessageHref()}
                onClick={() => setOpen(false)}
                className="text-[#5F6F52] hover:underline flex items-center gap-1"
              >
                Go to Messages &rarr;
              </Link>
              <Link
                href="/dashboard/notifications"
                onClick={() => setOpen(false)}
                className="text-[#5F6E66] hover:underline"
              >
                All Notifications
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
